import React, { useState, useContext, useEffect } from 'react';
import { AuthContext } from '../context/AuthContext';
import { useSidebar } from '../context/SidebarContext';
import SidePanel from '../components/Common/SidePanel';
import MobileMenuToggle from '../components/Common/MobileMenuToggle';
import ResponsiveTable from '../components/Common/ResponsiveTable';
import { UserPlus, Search, Edit2, Trash2, Loader, AlertCircle, X, Save, Eye, EyeOff } from 'lucide-react';
import { userService } from '../services/api';

const Users = () => {
  const { user } = useContext(AuthContext);
  const { isCollapsed } = useSidebar();
  const [isSidebarOpen, setIsSidebarOpen] = useState(false);
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  
  // Modal state
  const [showModal, setShowModal] = useState(false);
  const [modalMode, setModalMode] = useState('add'); // 'add' or 'edit'
  const [selectedUser, setSelectedUser] = useState(null);
  const [formData, setFormData] = useState({
    mobile: '',
    email: '',
    fullName: '',
    password: '',
    role: 'VEHICLE_EXECUTIVE',
    status: 'ACTIVE'
  });
  const [formError, setFormError] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [deleteConfirm, setDeleteConfirm] = useState(null);

  useEffect(() => {
    fetchUsers();
  }, []);

  const fetchUsers = async () => {
    try {
      setLoading(true);
      setError(null);
      const response = await userService.getAll();
      setUsers(response.data?.data?.users || []);
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to load users');
    } finally {
      setLoading(false);
    }
  };

  const openAddModal = () => {
    setModalMode('add');
    setFormData({
      mobile: '',
      email: '',
      fullName: '',
      password: '',
      role: 'VEHICLE_EXECUTIVE',
      status: 'ACTIVE'
    });
    setFormError('');
    setShowPassword(false);
    setShowModal(true);
  };

  const openEditModal = (selectedUser) => {
    setModalMode('edit');
    setSelectedUser(selectedUser);
    setFormData({
      mobile: selectedUser.mobile || '',
      email: selectedUser.email || '',
      fullName: selectedUser.fullName,
      password: '',
      role: selectedUser.role,
      status: selectedUser.status
    });
    setFormError('');
    setShowPassword(false);
    setShowModal(true);
  };

  const closeModal = () => {
    setShowModal(false);
    setSelectedUser(null);
    setFormData({
      mobile: '',
      email: '',
      fullName: '',
      password: '',
      role: 'VEHICLE_EXECUTIVE',
      status: 'ACTIVE'
    });
    setFormError('');
  };

  const validateForm = () => {
    if (!formData.mobile || !/^[0-9]{10}$/.test(formData.mobile)) {
      setFormError('Valid 10-digit mobile number is required');
      return false;
    }
    if (!formData.fullName || formData.fullName.trim().length < 2) {
      setFormError('Full name is required (minimum 2 characters)');
      return false;
    }
    if (modalMode === 'add' && (!formData.password || formData.password.length < 6)) {
      setFormError('Password is required (minimum 6 characters)');
      return false;
    }
    if (modalMode === 'edit' && formData.password && formData.password.length < 6) {
      setFormError('Password must be at least 6 characters');
      return false;
    }
    return true;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!validateForm()) {
      return;
    }

    setSubmitting(true);
    try {
      if (modalMode === 'add') {
        await userService.create({
          mobile: formData.mobile,
          email: formData.email || null,
          fullName: formData.fullName,
          password: formData.password,
          role: formData.role
        });
      } else {
        const updateData = {
          fullName: formData.fullName,
          role: formData.role,
          status: formData.status
        };
        if (formData.password) {
          updateData.password = formData.password;
        }
        // Note: Requires backend endpoint for update
        await userService.updateUser(selectedUser.id, updateData);
      }
      
      setFormError('');
      closeModal();
      await fetchUsers();
    } catch (err) {
      setFormError(err.response?.data?.error || `Failed to ${modalMode === 'add' ? 'create' : 'update'} user`);
    } finally {
      setSubmitting(false);
    }
  };

  const handleDelete = async (userId) => {
    try {
      await userService.deleteUser(userId);
      setDeleteConfirm(null);
      await fetchUsers();
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to delete user');
    }
  };

  const handleStatusChange = async (userId, currentStatus) => {
    try {
      const newStatus = currentStatus === 'ACTIVE' ? 'INACTIVE' : 'ACTIVE';
      await userService.updateStatus(userId, newStatus);
      await fetchUsers();
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to update user status');
    }
  };

  const filteredUsers = users.filter(u =>
    u.mobile?.toLowerCase().includes(searchTerm.toLowerCase()) ||
    u.fullName?.toLowerCase().includes(searchTerm.toLowerCase()) ||
    u.email?.toLowerCase().includes(searchTerm.toLowerCase())
  );

  if (loading) {
    return (
      <div className="flex h-screen bg-slate-50">
        <MobileMenuToggle onClick={() => setIsSidebarOpen(true)} isSidebarOpen={isSidebarOpen} />
        <SidePanel isOpen={isSidebarOpen} onClose={() => setIsSidebarOpen(false)} />
        <div className="flex-1 flex flex-col">
          <div className="flex-1 flex items-center justify-center">
            <Loader className="w-8 h-8 text-blue-600 animate-spin" />
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="flex h-screen bg-slate-50">
      <MobileMenuToggle onClick={() => setIsSidebarOpen(true)} isSidebarOpen={isSidebarOpen} />
      <SidePanel isOpen={isSidebarOpen} onClose={() => setIsSidebarOpen(false)} />
      <div className="flex-1 flex flex-col overflow-hidden transition-all duration-300">
        <main className="flex-1 overflow-y-auto pt-28 md:pt-0">
          <div className="p-6 md:p-8 max-w-7xl mx-auto">
            <div className="mb-8">
              <h1 className="text-3xl font-bold text-slate-900 mb-2">Users Management</h1>
              <p className="text-slate-600">Manage system users, roles, and permissions</p>
            </div>

            {error && (
              <div className="mb-6 p-4 bg-red-50 border border-red-200 rounded-lg flex gap-3 items-start">
                <AlertCircle className="w-5 h-5 text-red-600 flex-shrink-0 mt-0.5" />
                <div className="flex-1">
                  <p className="text-red-800">{error}</p>
                </div>
                <button onClick={() => setError(null)} className="text-red-600 hover:text-red-800">
                  <X className="w-5 h-5" />
                </button>
              </div>
            )}

            <div className="bg-white rounded-2xl shadow-lg border border-slate-200">
              <div className="p-6 border-b border-slate-200 flex justify-between items-center gap-4">
                <div className="relative flex-1 max-w-md">
                  <Search className="absolute left-3 top-3 w-5 h-5 text-slate-400" />
                  <input
                    type="text"
                    placeholder="Search by name or mobile..."
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                    className="w-full pl-10 pr-4 py-2 border border-slate-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                  />
                </div>
                <button
                  onClick={openAddModal}
                  className="flex items-center gap-2 bg-gradient-to-r from-blue-600 to-blue-700 text-white px-4 py-2 rounded-lg hover:shadow-lg transition"
                >
                  <UserPlus className="w-5 h-5" />
                  Add User
                </button>
              </div>

              <ResponsiveTable
                columns={[
                  { key: 'name', label: 'Name' },
                  { key: 'mobile', label: 'Mobile' },
                  { key: 'email', label: 'Email' },
                  { key: 'role', label: 'Role' },
                  { key: 'status', label: 'Status' },
                  { key: 'created', label: 'Created' },
                  { key: 'actions', label: 'Actions' }
                ]}
                data={filteredUsers}
                renderRow={(u, idx, isMobile) => {
                  if (isMobile) {
                    return (
                      <div key={u.id} className="space-y-3">
                        <div className="flex justify-between items-start">
                          <div>
                            <p className="font-semibold text-slate-900">{u.fullName}</p>
                            <p className="text-sm text-slate-600 font-mono">{u.mobile}</p>
                            <p className="text-xs text-slate-500 mt-1">{u.email || 'No email'}</p>
                          </div>
                          <div className="flex gap-2">
                            <button
                              onClick={() => openEditModal(u)}
                              className="p-2 hover:bg-blue-50 rounded-lg transition text-blue-600"
                              title="Edit user"
                            >
                              <Edit2 className="w-4 h-4" />
                            </button>
                            <button
                              onClick={() => setDeleteConfirm(u.id)}
                              className="p-2 hover:bg-red-50 rounded-lg transition text-red-600"
                              title="Delete user"
                            >
                              <Trash2 className="w-4 h-4" />
                            </button>
                          </div>
                        </div>
                        <div className="flex gap-2 flex-wrap">
                          <span className={`px-3 py-1 rounded-full text-sm font-medium ${
                            u.role === 'ADMIN' ? 'bg-red-100 text-red-800' :
                            u.role === 'SERVICE_ENGINEER' ? 'bg-purple-100 text-purple-800' :
                            'bg-blue-100 text-blue-800'
                          }`}>
                            {u.role?.replace(/_/g, ' ')}
                          </span>
                          <button
                            onClick={() => handleStatusChange(u.id, u.status)}
                            className={`px-3 py-1 rounded-full text-sm font-medium transition ${
                              u.status === 'ACTIVE'
                                ? 'bg-green-100 text-green-800 hover:bg-green-200'
                                : 'bg-slate-100 text-slate-600 hover:bg-slate-200'
                            }`}
                          >
                            {u.status}
                          </button>
                        </div>
                        <p className="text-xs text-slate-500">Created: {new Date(u.createdAt).toLocaleDateString()}</p>
                      </div>
                    );
                  }
                  
                  return (
                    <tr key={u.id} className="border-b border-slate-200 hover:bg-slate-50 transition">
                      <td className="px-6 py-4">
                        <p className="font-medium text-slate-900">{u.fullName}</p>
                      </td>
                      <td className="px-6 py-4 text-slate-600 font-mono">{u.mobile}</td>
                      <td className="px-6 py-4 text-slate-600 text-sm">{u.email || '-'}</td>
                      <td className="px-6 py-4">
                        <span className={`px-3 py-1 rounded-full text-sm font-medium ${
                          u.role === 'ADMIN' ? 'bg-red-100 text-red-800' :
                          u.role === 'SERVICE_ENGINEER' ? 'bg-purple-100 text-purple-800' :
                          'bg-blue-100 text-blue-800'
                        }`}>
                          {u.role?.replace(/_/g, ' ')}
                        </span>
                      </td>
                      <td className="px-6 py-4">
                        <button
                          onClick={() => handleStatusChange(u.id, u.status)}
                          className={`px-3 py-1 rounded-full text-sm font-medium transition ${
                            u.status === 'ACTIVE'
                              ? 'bg-green-100 text-green-800 hover:bg-green-200'
                              : 'bg-slate-100 text-slate-600 hover:bg-slate-200'
                          }`}
                        >
                          {u.status}
                        </button>
                      </td>
                      <td className="px-6 py-4 text-sm text-slate-600">
                        {new Date(u.createdAt).toLocaleDateString()}
                      </td>
                      <td className="px-6 py-4 text-right">
                        <div className="flex justify-end gap-2">
                          <button
                            onClick={() => openEditModal(u)}
                            className="p-2 hover:bg-blue-50 rounded-lg transition text-blue-600"
                            title="Edit user"
                          >
                            <Edit2 className="w-4 h-4" />
                          </button>
                          <button
                            onClick={() => setDeleteConfirm(u.id)}
                            className="p-2 hover:bg-red-50 rounded-lg transition text-red-600"
                            title="Delete user"
                          >
                            <Trash2 className="w-4 h-4" />
                          </button>
                        </div>
                      </td>
                    </tr>
                  );
                }}
              />
            </div>
          </div>
        </main>
      </div>

      {/* Add/Edit User Modal */}
      {showModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 z-50 flex items-center justify-center p-4">
          <div className="bg-white rounded-2xl shadow-xl max-w-md w-full max-h-[90vh] overflow-y-auto">
            {/* Header */}
            <div className="sticky top-0 bg-gradient-to-r from-blue-600 to-blue-700 text-white p-6 flex items-center justify-between">
              <h2 className="text-xl font-bold">
                {modalMode === 'add' ? 'Add New User' : 'Edit User'}
              </h2>
              <button
                onClick={closeModal}
                className="p-2 hover:bg-blue-500 rounded-lg transition"
              >
                <X className="w-5 h-5" />
              </button>
            </div>

            {/* Content */}
            <form onSubmit={handleSubmit} className="p-6 space-y-4">
              {formError && (
                <div className="p-3 bg-red-50 border border-red-200 rounded-lg flex gap-2">
                  <AlertCircle className="w-5 h-5 text-red-600 flex-shrink-0" />
                  <p className="text-red-800 text-sm">{formError}</p>
                </div>
              )}

              {/* Mobile */}
              <div>
                <label className="block text-sm font-semibold text-slate-700 mb-2">Mobile Number *</label>
                <input
                  type="tel"
                  required
                  disabled={modalMode === 'edit'}
                  value={formData.mobile}
                  onChange={(e) => setFormData({...formData, mobile: e.target.value.replace(/\D/g, '').slice(0, 10)})}
                  className="w-full px-4 py-2 border border-slate-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 disabled:bg-slate-100"
                  placeholder="Enter 10-digit mobile number"
                  maxLength="10"
                />
              </div>

              {/* Email (Optional) */}
              <div>
                <label className="block text-sm font-semibold text-slate-700 mb-2">Email (Optional)</label>
                <input
                  type="email"
                  value={formData.email}
                  onChange={(e) => setFormData({...formData, email: e.target.value})}
                  className="w-full px-4 py-2 border border-slate-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                  placeholder="Enter email (optional)"
                />
              </div>

              {/* Full Name */}
              <div>
                <label className="block text-sm font-semibold text-slate-700 mb-2">Full Name *</label>
                <input
                  type="text"
                  required
                  value={formData.fullName}
                  onChange={(e) => setFormData({...formData, fullName: e.target.value})}
                  className="w-full px-4 py-2 border border-slate-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                  placeholder="Enter full name"
                />
              </div>

              {/* Password */}
              <div>
                <label className="block text-sm font-semibold text-slate-700 mb-2">
                  Password {modalMode === 'edit' && '(leave blank to keep current)'}
                  {modalMode === 'add' && '*'}
                </label>
                <div className="relative">
                  <input
                    type={showPassword ? 'text' : 'password'}
                    required={modalMode === 'add'}
                    value={formData.password}
                    onChange={(e) => setFormData({...formData, password: e.target.value})}
                    className="w-full px-4 py-2 border border-slate-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                    placeholder={modalMode === 'add' ? 'Enter password' : 'New password (optional)'}
                  />
                  <button
                    type="button"
                    onClick={() => setShowPassword(!showPassword)}
                    className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-600 hover:text-slate-900"
                  >
                    {showPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                  </button>
                </div>
              </div>

              {/* Role */}
              <div>
                <label className="block text-sm font-semibold text-slate-700 mb-2">Role *</label>
                <select
                  required
                  value={formData.role}
                  onChange={(e) => setFormData({...formData, role: e.target.value})}
                  className="w-full px-4 py-2 border border-slate-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                >
                  <option value="VEHICLE_EXECUTIVE">Vehicle Executive</option>
                  <option value="SERVICE_ENGINEER">Service Engineer</option>
                  <option value="ADMIN">Admin</option>
                </select>
              </div>

              {/* Status (only for edit) */}
              {modalMode === 'edit' && (
                <div>
                  <label className="block text-sm font-semibold text-slate-700 mb-2">Status *</label>
                  <select
                    value={formData.status}
                    onChange={(e) => setFormData({...formData, status: e.target.value})}
                    className="w-full px-4 py-2 border border-slate-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                  >
                    <option value="ACTIVE">Active</option>
                    <option value="INACTIVE">Inactive</option>
                  </select>
                </div>
              )}

              {/* Action Buttons */}
              <div className="flex gap-3 pt-4 border-t border-slate-200">
                <button
                  type="submit"
                  disabled={submitting}
                  className="flex-1 flex items-center justify-center gap-2 bg-gradient-to-r from-blue-600 to-blue-700 text-white py-2 rounded-lg hover:shadow-lg transition disabled:opacity-50"
                >
                  <Save className="w-4 h-4" />
                  {submitting ? 'Saving...' : 'Save'}
                </button>
                <button
                  type="button"
                  onClick={closeModal}
                  className="flex-1 bg-slate-200 text-slate-900 py-2 rounded-lg hover:bg-slate-300 transition"
                >
                  Cancel
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Delete Confirmation Modal */}
      {deleteConfirm && (
        <div className="fixed inset-0 bg-black bg-opacity-50 z-50 flex items-center justify-center p-4">
          <div className="bg-white rounded-2xl shadow-xl max-w-sm w-full">
            <div className="bg-gradient-to-r from-red-600 to-red-700 text-white p-6">
              <h2 className="text-xl font-bold">Delete User</h2>
            </div>
            <div className="p-6">
              <p className="text-slate-700 mb-6">
                Are you sure you want to delete this user? This action cannot be undone.
              </p>
              <div className="flex gap-3">
                <button
                  onClick={() => handleDelete(deleteConfirm)}
                  className="flex-1 bg-red-600 text-white py-2 rounded-lg hover:bg-red-700 transition"
                >
                  Delete
                </button>
                <button
                  onClick={() => setDeleteConfirm(null)}
                  className="flex-1 bg-slate-200 text-slate-900 py-2 rounded-lg hover:bg-slate-300 transition"
                >
                  Cancel
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default Users;
